import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Factory, Loader2, Search } from 'lucide-react';
import { api } from '@/shared/api/cliente';
import { ErrorDeApi } from '@/shared/api/problema';
import { Modal } from '@/shared/ui/Modal';
import { notificar } from '@/shared/ui/avisos';
import { useSesion } from '@/shared/sesion/SesionProvider';
import { enUnidad, type CategoriaDeGenerador, type Generador } from './contrato';

interface Identidad {
  nombre: string;
  direccion?: string | null;
  telefono?: string | null;
  correo?: string | null;
}

export function ModalDeGenerador({
  generador,
  categorias,
  umbralKm,
  alCerrar,
  alGuardar,
}: {
  generador: Generador | null;
  categorias: CategoriaDeGenerador[];
  umbralKm: number;
  alCerrar: () => void;
  alGuardar: (g: Generador) => void;
}) {
  const { puede } = useSesion();
  const editando = generador !== null;

  const [identificacion, setIdentificacion] = useState('');
  const [nombre, setNombre] = useState('');
  const [direccion, setDireccion] = useState('');
  const [telefono, setTelefono] = useState(generador?.telefono ?? '');
  const [email, setEmail] = useState(generador?.email ?? '');
  const [categoriaId, setCategoriaId] = useState<number>(
    generador?.categoriaId ?? categorias[0]?.id ?? 1,
  );
  const [nombreComercial, setNombreComercial] = useState(generador?.nombreComercial ?? '');
  const [sucursal, setSucursal] = useState(generador?.sucursal ?? '');
  const [direccionRecoleccion, setDireccionRecoleccion] = useState(
    generador?.direccionRecoleccion ?? '',
  );
  const [distancia, setDistancia] = useState(String(generador?.distanciaKm ?? 0));
  const [observacion, setObservacion] = useState(generador?.observacion ?? '');
  const [activo, setActivo] = useState(generador?.activo ?? true);

  const categoria = categorias.find((c) => c.id === categoriaId);
  const km = Number(distancia.replace(',', '.'));
  const exceso = Number.isFinite(km) ? Math.max(0, km - umbralKm) : 0;

  const registroCivil = useMutation({
    mutationFn: () => api.get<Identidad>(`/identidades/${identificacion.trim()}`),
    onSuccess: (i) => {
      setNombre(i.nombre);
      if (i.direccion) setDireccion(i.direccion);
      if (i.telefono) setTelefono(i.telefono);
      if (i.correo) setEmail(i.correo);
    },
    onError: (causa) =>
      notificar.error(causa instanceof ErrorDeApi ? causa.message : 'NO SE PUDO CONSULTAR'),
  });

  const guardar = useMutation({
    mutationFn: () => {
      const datos = {
        categoriaId,
        nombreComercial,
        sucursal,
        direccionRecoleccion,
        distanciaKm: Number.isFinite(km) ? km : 0,
        observacion,
      };
      return editando
        ? api.put<Generador>(`/generadores/${generador.id}`, { ...datos, telefono, email, activo })
        : api.post<Generador>('/generadores', {
            identificacion: identificacion.trim(),
            nombre,
            direccion,
            telefono,
            email,
            ...datos,
          });
    },
    onSuccess: (g) => {
      notificar.exito(g.nombre, editando ? 'Generador actualizado' : 'Generador registrado');
      alGuardar(g);
    },
    onError: (causa) =>
      notificar.error(causa instanceof ErrorDeApi ? causa.message : 'NO SE PUDO GUARDAR'),
  });

  const listo =
    !guardar.isPending &&
    Number.isFinite(km) &&
    km >= 0 &&
    (editando || (/^\d{10}(\d{3})?$/.test(identificacion.trim()) && nombre.trim().length > 0));

  return (
    <Modal
      abierto
      alCerrar={alCerrar}
      titulo={editando ? 'Editar generador' : 'Nuevo generador'}
      subtitulo={editando ? generador.nombre : 'Quien paga por el peso de sus residuos'}
      icono={Factory}
      ancho="ancho"
      cerrarAlPulsarFuera={false}
    >
      <div className="grid gap-4">
        {editando ? (
          <div className="grid gap-3 rounded-xl border border-armazon-200 bg-armazon-50 px-4 py-3">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-sm font-bold uppercase text-armazon-800">
                  {generador.nombre}
                  {generador.sucursal && ` (${generador.sucursal})`}
                </p>
                <p className="importe text-xs text-armazon-500">{generador.identificacion}</p>
              </div>
              <span className="importe shrink-0 rounded-md bg-white px-2 py-1 text-[11px] font-semibold text-armazon-700 ring-1 ring-armazon-200">
                Código {generador.codigo}
              </span>
            </div>
            <div className="grid gap-3 sm:grid-cols-[12rem_1fr]">
              <Campo etiqueta="Teléfono">
                <input
                  value={telefono}
                  onChange={(e) => setTelefono(e.target.value)}
                  className={ENTRADA}
                />
              </Campo>
              <Campo etiqueta="Correo · a donde va su factura electrónica">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={ENTRADA}
                />
              </Campo>
            </div>
            <p className="text-[10px] uppercase text-armazon-400">
              El teléfono y el correo se guardan en el catastro. El nombre y la cédula o RUC se corrigen
              allí.
            </p>
          </div>
        ) : (
          <fieldset className="grid gap-3 rounded-xl border border-armazon-200 p-4">
            <legend className="px-1 text-[10px] font-semibold uppercase text-armazon-500">
              Quién paga · va al catastro
            </legend>
            <div className="grid gap-3 sm:grid-cols-[14rem_1fr]">
              <Campo etiqueta="RUC o cédula">
                <span className="flex gap-2">
                  <input
                    value={identificacion}
                    onChange={(e) => setIdentificacion(e.target.value.replace(/\D/g, ''))}
                    inputMode="numeric"
                    maxLength={13}
                    autoFocus
                    className={`${ENTRADA} importe`}
                  />
                  {puede('identidades.consultar') && (
                    <button
                      type="button"
                      onClick={() => registroCivil.mutate()}
                      disabled={!/^\d{10}(\d{3})?$/.test(identificacion) || registroCivil.isPending}
                      title="Traer los datos del Registro Civil o del SRI"
                      className="inline-flex h-10 shrink-0 items-center rounded-lg border border-armazon-200 px-3 text-armazon-600 transition-colors hover:bg-armazon-50 disabled:opacity-40"
                    >
                      {registroCivil.isPending ? (
                        <Loader2 className="size-4 animate-spin" aria-hidden />
                      ) : (
                        <Search className="size-4" aria-hidden />
                      )}
                    </button>
                  )}
                </span>
              </Campo>
              <Campo etiqueta="Razón social o nombre">
                <input
                  value={nombre}
                  onChange={(e) => setNombre(e.target.value.toUpperCase())}
                  className={ENTRADA}
                />
              </Campo>
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
              <Campo etiqueta="Dirección">
                <input
                  value={direccion}
                  onChange={(e) => setDireccion(e.target.value)}
                  className={ENTRADA}
                />
              </Campo>
              <Campo etiqueta="Teléfono">
                <input
                  value={telefono}
                  onChange={(e) => setTelefono(e.target.value)}
                  className={ENTRADA}
                />
              </Campo>
              <Campo etiqueta="Correo">
                <input
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={ENTRADA}
                />
              </Campo>
            </div>
          </fieldset>
        )}

        <Campo etiqueta="Categoría">
          <select
            value={categoriaId}
            onChange={(e) => setCategoriaId(Number(e.target.value))}
            className={`${ENTRADA} text-xs uppercase`}
          >
            {categorias.map((c) => (
              <option key={c.id} value={c.id}>
                {`${c.literal.toUpperCase()}) ${c.nombre} · $${c.tarifaUnitaria.toFixed(2)}/${enUnidad(c.unidad)}`}
              </option>
            ))}
          </select>
        </Campo>

        <div className="grid gap-3 sm:grid-cols-2">
          <Campo etiqueta="Nombre comercial">
            <input
              value={nombreComercial}
              onChange={(e) => setNombreComercial(e.target.value.toUpperCase())}
              className={ENTRADA}
            />
          </Campo>
          <Campo etiqueta="Sucursal · si el RUC tiene varios sitios">
            <input
              value={sucursal}
              onChange={(e) => setSucursal(e.target.value.toUpperCase())}
              placeholder="P. EJ. MERCADO MAYORISTA"
              className={ENTRADA}
            />
          </Campo>
        </div>
        <p className="-mt-2 text-[10px] uppercase text-armazon-400">
          Cada sucursal es un generador aparte con su código y su factura. Para añadir otro sitio de
          un RUC que ya es generador, regístrelo con el mismo RUC y el nombre del sitio.
        </p>

        <div className="grid items-start gap-3 sm:grid-cols-[1fr_12rem]">
          <Campo etiqueta="Dirección de recolección">
            <textarea
              value={direccionRecoleccion}
              onChange={(e) => setDireccionRecoleccion(e.target.value.toUpperCase())}
              rows={2}
              className={`${ENTRADA} h-auto resize-none py-2 leading-snug`}
            />
          </Campo>
          <Campo etiqueta="Distancia a EPAGAL (km)">
            <input
              value={distancia}
              onChange={(e) => setDistancia(e.target.value)}
              inputMode="decimal"
              className={`${ENTRADA} importe text-right`}
            />
          </Campo>
        </div>

        {categoria?.recargoPorDistancia ? (
          <p className="rounded-lg bg-blue-50 px-3 py-2 text-[11px] uppercase text-blue-800">
            {exceso > 0
              ? `Pasa ${exceso.toFixed(2)} km de los ${umbralKm}: cada recolección lleva un recargo por ellos (Art. 9 literal b).`
              : `Dentro de los ${umbralKm} km a la redonda de EPAGAL: sin recargo por distancia.`}
          </p>
        ) : (
          <p className="text-[10px] uppercase text-armazon-400">
            Esta categoría no lleva recargo por distancia: la ordenanza lo fija solo para el sector
            salud.
          </p>
        )}

        <Campo etiqueta="Observación">
          <input
            value={observacion}
            onChange={(e) => setObservacion(e.target.value)}
            className={ENTRADA}
          />
        </Campo>

        {editando && (
          <label className="flex items-center gap-2 text-xs font-semibold uppercase text-armazon-700">
            <input
              type="checkbox"
              checked={activo}
              onChange={(e) => setActivo(e.target.checked)}
              className="size-4 accent-marca-600"
            />
            Activo · aparece al registrar los pesajes del mes
          </label>
        )}

        <div className="flex justify-end gap-2 border-t border-armazon-100 pt-4">
          <button
            type="button"
            onClick={alCerrar}
            className="h-10 rounded-lg border border-armazon-200 px-4 text-xs font-semibold uppercase text-armazon-600 hover:bg-armazon-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={() => guardar.mutate()}
            disabled={!listo}
            className="inline-flex h-10 items-center gap-2 rounded-lg bg-marca-600 px-5 text-xs font-bold uppercase text-white shadow-sm hover:bg-marca-700 disabled:opacity-50"
          >
            {guardar.isPending && <Loader2 className="size-4 animate-spin" aria-hidden />}
            {editando ? 'Guardar cambios' : 'Registrar generador'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function Campo({ etiqueta, children }: { etiqueta: string; children: React.ReactNode }) {
  return (
    <label className="grid gap-1.5">
      <span className="text-[10px] font-semibold uppercase text-armazon-600">{etiqueta}</span>
      {children}
    </label>
  );
}

const ENTRADA =
  'h-10 w-full rounded-lg border border-armazon-200 bg-white px-3 text-sm text-armazon-900 outline-none transition-colors focus:border-marca-400';
